/**
 * `useErrorAction` —— 错误 `action` → 处理函数的**唯一路由**（ARCH §8.2「可执行动作」落地）。
 *
 * 设计要点：
 *  - 动作标签来源**唯一**：`errorCatalog[code].action`（`shared/errors.ts`），UI 禁硬编码 code→action 映射；
 *  - `topup`/`unlock`/`switchRelay` 走**内置**路由；`retry` 无法通用化，必须由发起方注入 `onAction`；
 *  - 无 handler（`none` / `undefined` / 无 `onAction` 的 `retry`）⇒ 返回 `null` ⇒ 调用方**不渲染按钮**（诚实降级）。
 *
 * `resolveErrorAction` 为**纯函数**（不触 React / Router / 服务），便于在 node 测试环境直接断言
 * 「动作→处理函数 / 是否渲染按钮」这一判定，而无需引入 RTL。
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ErrorAction } from '@shared/errors';
import { ROUTE_PATH } from '@/router';
import { getRelayService } from '@/services/session';

/** 动作按钮文案（与 `ErrorAction` 一一对应；`none` 不渲染，故为空串）。 */
export const ACTION_LABEL: Record<ErrorAction, string> = {
  retry: '重试',
  topup: '去充值',
  switchRelay: '切换中继',
  unlock: '解锁',
  none: '',
};

/**
 * 纯函数路由：解析 `action` → 处理函数；返回 `null` 表示**不渲染按钮**。
 *
 * @param action      取自 `errorCatalog[code].action`
 * @param onAction    发起方注入的处理器（`retry` 必传），优先级最高
 * @param navigate    路由跳转副作用（`topup` → `/wallet`；`unlock` → `/unlock`）
 * @param switchRelay 「切换中继」副作用
 */
export function resolveErrorAction(
  action: ErrorAction | undefined,
  onAction: (() => void) | undefined,
  navigate: (to: string) => void,
  switchRelay: () => void,
): (() => void) | null {
  if (onAction) return onAction;
  switch (action) {
    case 'topup':
      return () => navigate(ROUTE_PATH.wallet);
    case 'unlock':
      return () => navigate(ROUTE_PATH.unlock);
    case 'switchRelay':
      return switchRelay;
    case 'retry':
    case 'none':
    case undefined:
    default:
      return null;
  }
}

/** 组件内 hook：注入 Router 与中继服务副作用，得到 `action → handler` 解析器。 */
export function useErrorAction(): (
  action: ErrorAction | undefined,
  onAction?: () => void,
) => (() => void) | null {
  const navigate = useNavigate();
  return useCallback(
    (action, onAction) =>
      resolveErrorAction(
        action,
        onAction,
        (to) => navigate(to),
        () => {
          void getRelayService()?.switchRelay();
        },
      ),
    [navigate],
  );
}

export default useErrorAction;
